import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, MapPin, Phone, Star, ShieldCheck, Leaf, Users, Droplets, Clock, Sparkles,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Marquee } from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ServiceCard } from "@/components/ServiceCard";
import { BRAND, CATEGORIES, SERVICES, REVIEWS, HOME_FAQS, IMAGES, waLink } from "@/data/site";

const ease = [0.22, 1, 0.36, 1];

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-3 -mb-3">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Luxury spa setting with fresh towels, lotion and candles at Ody Luxurious Spa Manikonda"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-sand via-sand/40 to-sand/10" />
      <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-24 pt-44">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-bronze mb-8"
        >
          <span className="w-10 h-px bg-gold inline-block" />
          Ody Luxurious Spa — Manikonda, Hyderabad
        </motion.p>
        <h1 className="font-serif font-light tracking-tight text-ink text-5xl sm:text-7xl lg:text-8xl leading-[1.02]">
          <HeroLine delay={0.35}>Surrender to</HeroLine>
          <HeroLine delay={0.5}>the art of</HeroLine>
          <HeroLine delay={0.65}>
            <span className="italic text-bronze">stillness.</span>
          </HeroLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-stone2"
        >
          Seventeen signature services — from molten chocolate massages to 24K gold facials and
          private jacuzzi suites — crafted for the senses, delivered in absolute privacy.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={BRAND.tel}
            data-testid="hero-call-btn"
            className="group inline-flex items-center gap-4 rounded-full bg-ink text-cream px-8 py-4 transition-colors duration-300 hover:bg-bronze"
          >
            <span className="rounded-full bg-gold/20 p-2.5 text-gold transition-transform duration-300 group-hover:scale-110">
              <Phone size={17} />
            </span>
            <span className="flex flex-col items-start leading-tight text-left">
              <span className="text-[0.6rem] uppercase tracking-[0.28em] text-cream/60">
                Call to book your service
              </span>
              <span className="font-serif text-xl tracking-wide">{BRAND.phoneDisplay}</span>
            </span>
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,box-shadow] duration-300 hover:bg-wa-dark shadow-[0_12px_30px_rgba(37,211,102,0.35)]"
          >
            Book on WhatsApp
          </a>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-3 text-[0.7rem] uppercase tracking-[0.25em] text-stone2"
        >
          <span>17 Signature Services</span>
          <span>5 Curated Categories</span>
          <span>Private Suites Only</span>
        </motion.div>
      </motion.div>
    </section>
  );
};

const Philosophy = () => (
  <section className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <div className="relative">
        <Reveal>
          <div className="rounded-3xl overflow-hidden bg-alabaster">
            <img src={IMAGES.relaxation} alt="Spa ambience with reed diffuser, towels and candles" loading="lazy" decoding="async" className="w-full h-[420px] sm:h-[520px] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.25} className="absolute -bottom-8 -right-4 sm:-right-8 w-44 sm:w-56">
          <div className="float-slow rounded-2xl overflow-hidden border-4 border-sand bg-alabaster shadow-[0_20px_50px_rgba(31,29,26,0.18)]">
            <img src={IMAGES.ambience2} alt="Elegant private soak tub with greenery" loading="lazy" decoding="async" className="w-full h-40 sm:h-48 object-cover" />
          </div>
        </Reveal>
      </div>
      <div>
        <Reveal>
          <Eyebrow>The Ody Philosophy</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl leading-snug">
            Luxury is not gold leaf and marble. It is an hour where{" "}
            <span className="italic text-bronze">nothing is asked of you.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-stone2">
            Tucked above the bustle of Pipe Line Road in Manikonda, Ody Luxurious Spa was built
            around a single belief: true rest is rare, and it must be designed. Every suite is
            private, every therapist certified, every oil premium-grade — so the only thing left
            for you to do is let go.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <Link
            to="/about"
            data-testid="philosophy-about-link"
            className="group mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-bronze"
          >
            Our story
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const WHY = [
  [ShieldCheck, "Certified therapists", "Trained hands across Swedish, Thai, Balinese and deep tissue traditions."],
  [Leaf, "Premium organic oils", "Cold-pressed, skin-first botanicals in every single session."],
  [Users, "Private suites only", "No shared rooms, no thin curtains — your door, your hour."],
  [Droplets, "Hyper-hygienic", "Single-use linen, sealed products and sanitised suites, always."],
  [Sparkles, "Signature therapies", "Four-hand choreography, candle elixirs and 24K gold facials."],
  [Clock, "Unhurried, always", "Sessions scheduled with breathing room — nobody watches the clock."],
];

const WhyChooseUs = () => (
  <section data-testid="why-choose-us" className="py-24 sm:py-32 bg-alabaster">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow className="text-center">Why Choose Us</Eyebrow>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-5 text-center font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
          Why Hyderabad chooses Ody
        </h2>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-5 text-center text-sm sm:text-base text-stone2 max-w-2xl mx-auto">
          Six quiet promises behind every booking at Manikonda's most loved luxury spa.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {WHY.map(([Icon, title, text], i) => (
          <Reveal key={title} delay={(i % 3) * 0.08}>
            <div className="h-full rounded-2xl border border-bronze/10 bg-cream p-7 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-bronze/30 hover:shadow-[0_20px_45px_rgba(140,90,60,0.1)]">
              <span className="inline-flex rounded-full bg-bronze/10 p-3 text-bronze">
                <Icon size={18} />
              </span>
              <h3 className="mt-4 font-serif text-xl text-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-stone2">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Categories = () => (
  <section className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <Reveal>
            <Eyebrow>Five Worlds of Calm</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
              Choose your service
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/services"
            data-testid="categories-view-all"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-bronze"
          >
            All 17 services
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5">
        {CATEGORIES.map((cat, i) => (
          <Reveal
            key={cat.slug}
            delay={i * 0.08}
            className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}
          >
            <Link
              to={`/services/${cat.slug}`}
              data-testid={`category-card-${cat.slug}`}
              className="group relative block rounded-2xl overflow-hidden h-72 sm:h-80 bg-alabaster"
            >
              <img
                src={cat.image}
                alt={`${cat.name} at Ody Luxurious Spa Manikonda`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-ink/35 transition-colors duration-500 group-hover:bg-ink/50" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="text-[0.65rem] uppercase tracking-[0.3em] text-gold mb-2">{cat.eyebrow}</p>
                <h3 className="font-serif text-2xl sm:text-3xl text-cream">{cat.name}</h3>
                <p className="mt-1.5 text-sm text-cream/75">{cat.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] text-cream/90 opacity-0 translate-y-2 transition-[opacity,transform] duration-500 group-hover:opacity-100 group-hover:translate-y-0">
                  Discover <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Signatures = () => {
  const featured = ["ody-couple", "24k-gold-facial", "four-hand", "chocolate"]
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);
  return (
    <section className="py-24 sm:py-32 bg-alabaster">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow className="text-center">The Signature Collection</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 text-center font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
            Services guests cross the city for
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewsTeaser = () => (
  <section className="py-24 sm:py-32 bg-ink text-cream">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-gold">Guest Words</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif font-normal tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Loved by Hyderabad
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/reviews"
            data-testid="reviews-teaser-link"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold"
          >
            Read all reviews
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {REVIEWS.slice(0, 3).map((r, i) => (
          <Reveal key={r.name} delay={i * 0.1}>
            <figure className="h-full rounded-2xl border border-cream/10 bg-cream/[0.04] p-7">
              <div className="flex gap-1 text-gold" aria-label="5 star review">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={14} fill="currentColor" />
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-cream/80">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-6">
                <p className="font-serif text-lg text-cream">{r.name}</p>
                <p className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mt-1">{r.service}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const BookingCta = () => (
  <section data-testid="booking-cta" className="relative py-24 sm:py-32 overflow-hidden">
    <div className="absolute inset-0 bg-alabaster">
      <img
        src={IMAGES.ambience2}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-ink/65" />
    </div>
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center text-cream">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-gold">Begin Today</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">
          Your table is <span className="italic text-gold">waiting.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-5 text-sm sm:text-base text-cream/75 max-w-xl mx-auto">
          Pick your service, duration and time slot — your booking request reaches our WhatsApp
          instantly, and we confirm within minutes.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/contact#booking"
            data-testid="booking-cta-btn"
            className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-cream"
          >
            Book a Session
          </Link>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-cta-whatsapp"
            className="inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-wa-dark"
          >
            WhatsApp Us
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const HomeFaq = () => (
  <section className="py-24 sm:py-32">
    <div className="max-w-3xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow className="text-center">Before You Ask</Eyebrow>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-5 text-center font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
          A few quick answers
        </h2>
      </Reveal>
      <div className="mt-12 space-y-8">
        {HOME_FAQS.map((f, i) => (
          <Reveal key={f.q} delay={i * 0.08}>
            <div className="border-b border-bronze/10 pb-8">
              <h3 className="font-serif text-xl text-ink">{f.q}</h3>
              <p className="mt-3 text-sm leading-relaxed text-stone2">{f.a}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10 text-center">
          <Link
            to="/faq"
            data-testid="home-faq-link"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-bronze"
          >
            All questions
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const LocationCta = () => (
  <section className="relative py-24 sm:py-32 overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.massageCta} alt="" aria-hidden="true" loading="lazy" decoding="async" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-ink/70" />
    </div>
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center text-cream">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-gold">Find Your Way to Calm</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">
          First floor, Plot No. 8, Pipe Line Road,
          <span className="italic"> beside RajShree Jewellers</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-5 text-sm sm:text-base text-cream/70">
          Rajender Nagar, Manikonda, Hyderabad — Telangana 500089
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a
            href={BRAND.maps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-directions-btn"
            className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-cream"
          >
            <MapPin size={15} /> Get Directions
          </a>
          <a
            href={BRAND.tel}
            data-testid="home-call-btn"
            className="inline-flex items-center gap-3 rounded-full border border-cream/40 px-8 py-4 text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-cream hover:text-ink"
          >
            <Phone size={15} /> {BRAND.phoneDisplay}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Ody Luxurious Spa | Luxury Spa & Massage in Manikonda, Hyderabad"
        description="Best luxury spa in Manikonda, Hyderabad — Swedish, Thai, deep tissue & Balinese massages, couple spa, gold facials, body wraps & private jacuzzi. Call +91 90147 29756."
        keywords="luxury spa Manikonda, spa in Hyderabad, massage Manikonda, couple massage Hyderabad, Swedish massage, Thai massage, deep tissue massage, Balinese massage, gold facial, body wrap, foot reflexology, private jacuzzi Hyderabad"
      />
      <Hero />
      <Marquee />
      <Philosophy />
      <Categories />
      <Signatures />
      <WhyChooseUs />
      <ReviewsTeaser />
      <BookingCta />
      <HomeFaq />
      <LocationCta />
    </>
  );
}
